import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

export type HostProfile = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  bio?: string | null;
  member_type?: string | null;
  joined_at?: string | null;
  created_at?: string | null;
};

/**
 * Fetch host display info (name, avatar, bio) for a set of host_ids — event
 * hosts and departure-wave hosts. Uses the `public_host_profiles` view so this
 * works for anon/auth users without exposing sensitive profile columns.
 */
export const useHostProfiles = (hostIds: (string | null | undefined)[]) => {
  const uniqueIds = Array.from(
    new Set(hostIds.filter((id): id is string => !!id))
  ).sort();

  return useQuery({
    queryKey: ["host-profiles", uniqueIds],
    enabled: uniqueIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("public_host_profiles" as any)
        .select("id, first_name, last_name, avatar_url, bio, member_type, joined_at, created_at")
        .in("id", uniqueIds);

      if (error) {
        console.error("Failed to load host profiles", error);
        return {} as Record<string, HostProfile>;
      }

      const map: Record<string, HostProfile> = {};
      ((data ?? []) as unknown as HostProfile[]).forEach((p) => {
        map[p.id] = p;
      });
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });
};

/** Merge host profiles into events under `event.profiles` / `event.secondary_profiles`. */
export const mergeHostsIntoEvents = <
  T extends {
    host_id?: string | null;
    secondary_host_id?: string | null;
    profiles?: any;
    secondary_profiles?: any;
  }
>(
  events: T[] | undefined,
  hostMap: Record<string, HostProfile> | undefined
): T[] => {
  if (!events) return [];
  if (!hostMap) return events;
  return events.map((e) => {
    const merged: T = { ...e };
    if (e.host_id && hostMap[e.host_id]) merged.profiles = hostMap[e.host_id];
    if (e.secondary_host_id && hostMap[e.secondary_host_id]) {
      merged.secondary_profiles = hostMap[e.secondary_host_id];
    }
    return merged;
  });
};
